import React from 'react';
import dayjs from 'dayjs';
import { DatePicker, Tooltip } from 'antd';
import { MapPin, Calendar, UserRound, Users, Tags } from 'lucide-react';
import { TagsDisplay } from '../../../tags/elements';
import { User } from '../../../../data';
import { StatusTag } from '../../../common';

const { RangePicker } = DatePicker;

/** One team fact (Gerente / Asesores / Etiquetas) with a quiet icon chip. */
function TeamFact({ icon, label, children }) {
    return (
        <div className="flex items-start gap-3 min-w-0">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-100 text-[15px] text-surface-500">{icon}</span>
            <div className="flex-1 min-w-0">
                <span className="block mb-1 text-xs font-medium text-surface-500">{label}</span>
                {children}
            </div>
        </div>
    );
}

function StoreTitle({ store, period, onPeriodChange }) {
    const isClientUser = User.getInstance()?.userType === 'client';
    const today = dayjs();
    const hasCoordinates = store?.coordinates?.latitude && store?.coordinates?.longitude;

    return (
        <section className="shelf-card">
            {/* Identity + analysis period */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-5 p-5">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <StatusTag tone={store?.active ? 'success' : 'danger'} dot>
                            {store?.active ? 'Activo' : 'Inactivo'}
                        </StatusTag>
                        <StatusTag tone={store?.channel === 'tradicional' ? 'warning' : 'info'}>
                            {store?.channel === 'tradicional' ? 'Tradicional' : 'Moderno'}
                        </StatusTag>
                        {hasCoordinates && (
                            <Tooltip title={`${store.coordinates.latitude}, ${store.coordinates.longitude}`}>
                                <span>
                                    <StatusTag tone="violet" icon={<MapPin />}>
                                        GPS
                                    </StatusTag>
                                </span>
                            </Tooltip>
                        )}
                    </div>
                    <h2 className="m-0 text-xl font-semibold tracking-tight text-surface-900">{store?.name}</h2>
                    <p className="m-0 mt-1 text-sm text-surface-500">
                        RIF: {store?.rif} · {store?.clientName || 'Sin cliente'}
                    </p>
                </div>

                {/* Date range picker */}
                <div className="flex flex-col gap-1.5">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-surface-500">
                        <Calendar />
                        Período de análisis
                    </span>
                    <RangePicker
                        maxDate={today}
                        onChange={(e) => onPeriodChange(e)}
                        value={period || [today.startOf('month'), today.endOf('month')]}
                        allowClear={false}
                        format="DD/MM/YYYY"
                    />
                </div>
            </div>

            {/* Team */}
            <div className="grid gap-5 grid-cols-1 md:grid-cols-3 px-5 py-4 border-t border-surface-100">
                <TeamFact icon={<UserRound />} label="Gerente">
                    <span className="block truncate text-sm font-semibold text-surface-900">{store?.gerente || 'Sin asignar'}</span>
                </TeamFact>

                <TeamFact icon={<Users />} label="Asesores">
                    {store?.asesor && store.asesor.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                            {store.asesor.map((asesor, idx) => (
                                <StatusTag key={idx} tone="brand">
                                    {asesor?.name}
                                </StatusTag>
                            ))}
                        </div>
                    ) : (
                        <span className="text-sm text-surface-400">Sin asignar</span>
                    )}
                </TeamFact>

                <TeamFact icon={<Tags />} label="Etiquetas">
                    {store?._id && <TagsDisplay modelType="Store" modelId={store._id} editable={!isClientUser} max={Infinity} />}
                </TeamFact>
            </div>
        </section>
    );
}

export default StoreTitle;
